import { useCallback, useContext } from 'react';
import { When } from 'react-if';
import { EffectContext } from '../../../common/view/effect/effect.context';
import { ImmerFuncs } from '../../../external/immer/immer.funcs';
import { Luxon } from '../../../external/luxon/luxon';
import { LuxonFuncs } from '../../../external/luxon/luxon.funcs';
import { NodeWire } from '../../../external/server/node.wire';
import { ServerApi } from '../../../external/server/server.api';
import { CssFuncs } from '../../../foundation/css/css.funcs';
import { ArrayFuncs } from '../../../foundation/js/array.funcs';
import { JsFuncs } from '../../../foundation/js/js.funcs';
import { EffectTypeWire } from '../../core/effect/effect-type.wire';
import { EffectWire } from '../../core/effect/effect.wire';
import { EffectWireFuncs } from '../../core/effect/effect.wire.func';
import { ProgressEffectDataWire } from '../../core/effect/effects/progress/progress-effect-data.wire';
import { SnoozeEffectDataWire } from '../../core/effect/effects/snooze/snooze-effect-data.wire';
import { NodeDoneHistoryWireFuncs } from '../../core/node/node-done-history.wire.funcs';
import { NodeStore } from '../../core/node/node.store';
import { ProgressEffectCurrentEditModal } from '../effect/effects/progress/progress-effect-current-edit-modal';
import { NodeItemActionButtonStyle } from './node-item-action-button.style';
import { NodeItemActionCheck } from './node-item-action-check';
import { NodeItemActionSnooze } from './node-item-action-snooze';

export function NodeItemActionButton({ nodeId }: { nodeId: string }) {
  const node = NodeStore.useItem(nodeId);
  const { modalApi } = useContext(EffectContext);

  const effects = ArrayFuncs.ensure(node?.effects);
  const progressEffect = EffectWireFuncs.findEffect<ProgressEffectDataWire>({ effects, type: EffectTypeWire.progress });
  const hasProgressEffect = JsFuncs.isNotNull(progressEffect);
  const isPartialFinished = progressEffect && progressEffect.data.current > 0 && progressEffect.data.current < progressEffect.data.total;
  const showCheckedForPartialFinish = isPartialFinished && LuxonFuncs.isInToday(node?.doneAt ?? 0);

  const snoozeEffect = EffectWireFuncs.findEffect<SnoozeEffectDataWire>({ effects, type: EffectTypeWire.snooze });
  const hasSnoozeEffect = JsFuncs.isNotNull(snoozeEffect);
  const snoozeEffectIsActive = hasSnoozeEffect && Luxon.DateTime.now() < Luxon.DateTime.fromFormat(snoozeEffect!.data.date, 'yyyy-MM-dd') && !node.done;

  const handleCheck = useCallback(
    async (checked: boolean) => {
      if (!nodeId) {
        return;
      }

      if (hasProgressEffect) {
        const newProgressEffect = await new Promise<EffectWire<ProgressEffectDataWire>>((resolve) => {
          modalApi.push({
            component: (
              <ProgressEffectCurrentEditModal
                effect={progressEffect!}
                onOk={(e) => {
                  modalApi.pop();
                  resolve(e);
                }}
              />
            ),
          });
        });

        const newEffects = ImmerFuncs.produce(effects, (draft) => {
          EffectWireFuncs.updateEffect({ effects: draft, effect: newProgressEffect });
        });

        const partialNode: Partial<NodeWire> = {
          effects: newEffects,
          doneAt: Date.now(),
          done: newProgressEffect.data.current >= newProgressEffect.data.total,
        };

        NodeStore.updateItem(nodeId, partialNode);
        await ServerApi.updateNode(nodeId, partialNode);
        //
      } else {
        const now = Date.now();
        const partialNode: Partial<NodeWire> = checked ? { done: true, doneAt: now } : { done: false };

        NodeStore.updateItem(nodeId, partialNode);
        await ServerApi.updateNode(nodeId, partialNode);
      }

      if (checked) {
        const nodeDoneHistory = NodeDoneHistoryWireFuncs.init({
          nodeId,
          doneAt: Luxon.DateTime.now().toMillis(),
          text: node?.text,
        });
        await ServerApi.createNodeDoneHistory(nodeDoneHistory);
      }
    },
    [node],
  );

  const isPlaceholder = !node;

  return (
    <NodeItemActionButtonStyle.Container
      className={CssFuncs.className((names) => {
        if (snoozeEffectIsActive) {
          names.push('snooze-enabled');
        }
      })}
    >
      <NodeItemActionButtonStyle.CheckItem>
        <NodeItemActionCheck checked={node?.done || showCheckedForPartialFinish} disabled={isPlaceholder} onChange={handleCheck} />
      </NodeItemActionButtonStyle.CheckItem>
      <When condition={snoozeEffectIsActive}>
        <NodeItemActionButtonStyle.SnoozeItem>
          <NodeItemActionSnooze />
        </NodeItemActionButtonStyle.SnoozeItem>
      </When>
    </NodeItemActionButtonStyle.Container>
  );
}
